import { SyntenyTypeEnum } from "@selector/shared/openapi";
import { clsx } from "clsx";
import fuzzysort from "fuzzysort";
import { type FC, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import { Callout, CalloutIntent, ColoredBullet, HStack, SearchField, Switch, TruncateText, VStack } from "widgets";
import { useShallow } from "zustand/shallow";

import { getAlignedContigs } from "../../api/fetch.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import style from "./ContigSelectionCombobox.module.css";

interface ContigSelectionComboboxProps {
  groupId: string;
  close?: VoidFunction;
  maxHeight: number;
}

export const ContigSelectionCombobox: FC<ContigSelectionComboboxProps> = ({ groupId, close, maxHeight }) => {
  const refreshTracks = useGenomeViewerStore(useShallow((state) => state.refreshTracks));
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);
  const trackGroup = useGenomeViewerStore(useShallow((state) => state.trackGroups.get(groupId)))!;
  const syntenyOrgId = useGenomeViewerStore(useShallow((state) => state.syntenyOrganism.id));
  const activeSyntenyType = useGenomeViewerStore(useShallow((state) => state.activeSyntenyType));
  const contigs = useGenomeViewerStore(useShallow((state) => state.organisms.get(trackGroup.organismId)?.contigs));
  const updateTrackContigId = useGenomeViewerStore((state) => state.updateTrackContigId);
  const [error, setError] = useState<Error | null>(null);
  const [alignedContigs, setAlignedContigs] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [withSyntelogs, setWithSyntelogs] = useState(false);
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);
  const listRef = useRef<List>(null);

  useEffect(() => {
    setError(null);

    if (syntenyOrgId && activeSyntenyType) {
      const fetchAlignedContigs = async () => {
        const res = await getAlignedContigs(syntenyOrgId, trackGroup.organismId, activeSyntenyType).catch(setError);
        startTransition(() => {
          setAlignedContigs(res ?? []);
        });
      };

      void fetchAlignedContigs();
    }
  }, [syntenyOrgId, trackGroup.organismId, activeSyntenyType]);

  const results = useMemo(() => {
    const data = [...(contigs?.values() ?? [])]
      .map((contig) => ({
        value: contig.id,
        label: contig.name,
        hasAlignment: alignedContigs?.includes(contig.id) ?? false,
      }))
      .filter((contig) => (withSyntelogs ? contig.hasAlignment : true));

    return fuzzysort.go(search.trim(), data, { key: "label", all: true });
  }, [alignedContigs, contigs, withSyntelogs, search]);

  const deferredResults = useDeferredValue(results);

  // Scroll to selected item only once when component opens and data is available
  useEffect(() => {
    if (!hasInitialScrolled && deferredResults.length > 0) {
      const idx = deferredResults.findIndex((c) => c.obj.value === trackGroup.contigId);
      if (idx !== -1) {
        listRef.current?.scrollToItem(idx, "smart");
        setHasInitialScrolled(true);
      }
    }
  }, [deferredResults, trackGroup.contigId, hasInitialScrolled]);

  const handleSearchChange = useCallback((value: string) => {
    startTransition(() => {
      setSearch(value);
    });
  }, []);

  const handleContigSelect = useCallback(
    (contigId: number) => {
      startTransition(() => {
        updateTrackContigId(groupId, contigId);
        refreshTracks();
        refreshMain();
        close?.();
      });
    },
    [updateTrackContigId, groupId, refreshTracks, refreshMain, close],
  );

  const itemHeight = 28;
  const listMaxHeight = maxHeight === 0 ? 250 : Math.min(maxHeight - 94, 250);
  const calculatedHeight = Math.min(deferredResults.length * itemHeight, listMaxHeight);

  return (
    <VStack>
      <SearchField label="Contig" onChange={handleSearchChange} autoFocus isRequired width="100%" />
      <Switch
        label={
          activeSyntenyType == SyntenyTypeEnum.GENOME_ALIGNMENT ? "Only with Syntenic Regions" : "Only with Syntelogs"
        }
        onChange={setWithSyntelogs}
        isSelected={withSyntelogs}
      />
      {/* We use react-window because react-aria can not handle large data on combobox, see
          - SEL11-166
          - https://github.com/adobe/react-spectrum/issues/6981
          - https://github.com/adobe/react-spectrum/issues/5897
      */}
      <List
        height={calculatedHeight}
        itemCount={deferredResults.length}
        itemSize={itemHeight}
        width="100%"
        className={style.list}
        ref={listRef}
      >
        {({ index, style: css }) => {
          const data = deferredResults[index].obj;

          return (
            <HStack
              style={css}
              className={clsx(style.item, { [style.item_active]: data.value === trackGroup.contigId })}
              domProps={{
                onClick: () => handleContigSelect(data.value),
              }}
            >
              <TruncateText className={style.label} style={{ flex: "0 1 auto" }} text={data.label} />
              <ColoredBullet
                color={data.hasAlignment ? "var(--primary-light)" : "transparent"}
                className={style.colorBullet}
              />
            </HStack>
          );
        }}
      </List>
      {!!error && (
        <Callout title="Loading Error" intent={CalloutIntent.Danger}>
          {error.message}
        </Callout>
      )}
    </VStack>
  );
};
